/**
 * Push notification handler for Firebase Cloud Messaging (web).
 *
 * This module manages:
 *  - Service worker registration
 *  - FCM token retrieval and backend registration
 *  - Foreground message handling via a pluggable callback
 *
 * ## Customizing the notification UI
 *
 * The default handler (`defaultOnForegroundMessage`) is a simple callback that
 * the PushNotificationProvider uses to show a MUI modal. To replace it with a
 * toast, snackbar, or custom component:
 *
 *   1. Call `setOnForegroundMessage(yourHandler)` before `initPushNotifications()`,
 *      or pass your handler to the `<PushNotificationProvider onMessage={...}>`.
 *   2. Your handler receives a `PushNotificationPayload` and can do whatever it wants.
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { getFirebaseApp } from './firebase';
import { apiFetch } from './api-client';

export type PushNotificationPayload = {
  title: string;
  body: string;
  data: Record<string, string>;
};

type ForegroundMessageHandler = (payload: PushNotificationPayload) => void;

let foregroundHandler: ForegroundMessageHandler | null = null;
let currentToken: string | null = null;
let initialized = false;

/** Override the default foreground message handler. */
export function setOnForegroundMessage(handler: ForegroundMessageHandler) {
  foregroundHandler = handler;
}

/**
 * Returns the current FCM token, or null if not yet initialized.
 *
 * @public — part of the documented push API surface; no in-repo caller today.
 */
export function getFcmToken(): string | null {
  return currentToken;
}

/**
 * Initialize push notifications:
 *  1. Register the Firebase messaging service worker
 *  2. Request notification permission
 *  3. Get the FCM token
 *  4. Register the token with the backend
 *  5. Listen for foreground messages
 *
 * Returns the FCM token on success, or null if the user denied permission
 * or Firebase config is missing.
 */
export async function initPushNotifications(): Promise<string | null> {
  if (initialized && currentToken) {
    return currentToken;
  }
  if (typeof window === 'undefined') {
    return null;
  }

  const app = getFirebaseApp();
  if (!app) {
    // biome-ignore lint/suspicious/noConsole: push notification diagnostics
    console.warn('[push] Firebase not configured — skipping push init');
    return null;
  }

  // Register service worker — Firebase config is hardcoded in the SW file
  // so that push/notificationclick handlers are registered at top level.
  const registration = await navigator.serviceWorker.register(
    '/firebase-messaging-sw.js'
  );
  await navigator.serviceWorker.ready;

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    // biome-ignore lint/suspicious/noConsole: push notification diagnostics
    console.info('[push] Notification permission denied');
    return null;
  }

  // Get FCM token
  const messaging = getMessaging(app);
  const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;
  const token = await getToken(messaging, {
    serviceWorkerRegistration: registration,
    vapidKey: vapidKey || undefined
  });

  if (!token) {
    // biome-ignore lint/suspicious/noConsole: push notification diagnostics
    console.warn('[push] Failed to get FCM token');
    return null;
  }

  currentToken = token;
  // biome-ignore lint/suspicious/noConsole: push notification diagnostics
  console.info('[push] FCM token obtained:', token.substring(0, 20) + '...');

  // Register with backend
  await registerTokenWithBackend(token);

  // Listen for foreground messages
  onMessage(messaging, (payload) => {
    // biome-ignore lint/suspicious/noConsole: push notification diagnostics
    console.info(
      '[push] Foreground message received:',
      JSON.stringify(payload)
    );

    const notification: PushNotificationPayload = {
      title: payload.notification?.title ?? 'Notification',
      body: payload.notification?.body ?? '',
      data: (payload.data as Record<string, string>) ?? {}
    };

    if (foregroundHandler) {
      foregroundHandler(notification);
    } else {
      // biome-ignore lint/suspicious/noConsole: push notification diagnostics
      console.warn(
        '[push] No foreground handler registered — notification dropped'
      );
    }
  });

  initialized = true;
  return token;
}

/** Unregister the current token from the backend (call on sign-out). */
export async function unregisterPushNotifications(): Promise<void> {
  if (!currentToken) {
    return;
  }

  try {
    await apiFetch(
      `/api/v1/notifications/unregister-device?deviceToken=${encodeURIComponent(currentToken)}`,
      { method: 'DELETE' }
    );
  } catch (err) {
    // biome-ignore lint/suspicious/noConsole: push notification diagnostics
    console.warn('[push] Failed to unregister device token', err);
  }

  currentToken = null;
  initialized = false;
}

async function registerTokenWithBackend(token: string): Promise<void> {
  try {
    await apiFetch('/api/v1/notifications/register-device', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceToken: token, platform: 'WEB' })
    });
  } catch (err) {
    // biome-ignore lint/suspicious/noConsole: push notification diagnostics
    console.error('[push] Failed to register device token with backend', err);
  }
}
